import { Image as ImageIcon } from "lucide-react-native";
import { ReactNode } from "react";
import { Platform } from "react-native";

import { Text } from "@/components/ui";
import { Cell, VariableDef } from "@/types/resources";

const TOKEN_REGEX = /\{\{\s*([^{}]*?)\s*\}\}/g;

export type VariableType = VariableDef["type"];

export interface VariableSegment {
  key: string;
  known: boolean;
  text: string;
  token: boolean;
  /** Declared type of a known variable token; null for plain text / unknown. */
  type: VariableType | null;
}

/** Declared variables as a name → type map (trimmed, de-blanked). Carries the
    type so image tokens can render distinctly from text ones. */
export const variableTypeMap = (
  variables: VariableDef[]
): Map<string, VariableType> => {
  const map = new Map<string, VariableType>();
  variables.forEach((v) => {
    const name = v.name.trim();
    if (name) map.set(name, v.type);
  });
  return map;
};

/** Split text into plain and `{{token}}` segments, flagging known variables and
    carrying each known token's declared type. */
export const toVariableSegments = (
  value: string,
  types: Map<string, VariableType>
): VariableSegment[] => {
  const segments: VariableSegment[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  TOKEN_REGEX.lastIndex = 0;
  while ((match = TOKEN_REGEX.exec(value)) !== null) {
    if (match.index > last) {
      segments.push({
        key: `t${i}`,
        known: false,
        text: value.slice(last, match.index),
        token: false,
        type: null,
      });
    }
    const name = match[1].trim();
    segments.push({
      key: `v${i}`,
      known: types.has(name),
      text: match[0],
      token: true,
      type: types.get(name) ?? null,
    });
    last = match.index + match[0].length;
    i++;
  }
  if (last < value.length) {
    segments.push({
      key: `t${i}`,
      known: false,
      text: value.slice(last),
      token: false,
      type: null,
    });
  }
  return segments;
};

/**
 * Inline, visible highlighting for read-mode display: known text `{{variables}}`
 * render in the primary color, image variables in the info color with a small
 * image glyph (web) so they read as a media placeholder, unknown ones in red.
 * Returns `<Text>` spans meant to be nested inside a parent `<Text>` (so they
 * inherit its size).
 */
export const renderVariableText = (
  value: string,
  types: Map<string, VariableType>
): ReactNode[] =>
  toVariableSegments(value, types).map((s) => {
    if (!s.token) return <Text key={s.key}>{s.text}</Text>;
    if (s.type === "image") {
      return (
        <Text key={s.key} className="text-info-600">
          {Platform.OS === "web" && (
            <ImageIcon
              color="rgb(var(--color-info-600))"
              size={13}
              // Web-only: flow the glyph inline with the token text instead of
              // as its own block (which forces a line break), nudged onto the
              // text baseline. These CSS values aren't valid RN `ViewStyle`.
              // @ts-expect-error - web inline layout for the SVG glyph
              style={{
                display: "inline-block",
                marginRight: 2,
                verticalAlign: "-2px",
              }}
            />
          )}
          {s.text}
        </Text>
      );
    }
    return (
      <Text
        key={s.key}
        className={s.known ? "text-primary-600" : "text-error-600"}
      >
        {s.text}
      </Text>
    );
  });

/** Bare variable name inside a `{{token}}` segment (strips braces + padding). */
const tokenName = (token: string): string =>
  token.replace(/^\{\{\s*|\s*\}\}$/g, "");

/**
 * Results-mode rendering: substitute each text `{{variable}}` with the given
 * cell's value (highlighted in the primary color so the injected content reads
 * as data), drop image `{{variable}}` tokens entirely (the cell's images are
 * shown separately), and leave unknown tokens as plain text. Returns `<Text>`
 * spans meant to nest inside a parent `<Text>`.
 */
export const renderCellText = (
  value: string,
  variables: VariableDef[],
  cell: Cell
): ReactNode[] => {
  const types = variableTypeMap(variables);
  return toVariableSegments(value, types).flatMap((s) => {
    if (!s.token) return [<Text key={s.key}>{s.text}</Text>];
    if (s.type === "image") return [];
    if (s.type === "text") {
      const raw = cell[tokenName(s.text)];
      const filled = typeof raw === "string" && raw.trim().length > 0;
      return [
        <Text key={s.key} className="text-primary-600">
          {filled ? raw : s.text}
        </Text>,
      ];
    }
    return [<Text key={s.key}>{s.text}</Text>];
  });
};

/** Presigned URLs of the cell images whose `{{token}}` appears in `texts`, in
    first-seen order — as in the simulation, an image the question doesn't
    reference is never shown. Prefers the read-time `<name>_url` sibling,
    falling back to the raw key. */
export const cellImageUrls = (
  texts: string[],
  variables: VariableDef[],
  cell: Cell
): string[] => {
  const types = variableTypeMap(variables);
  return texts
    .flatMap((text) => toVariableSegments(text, types))
    .filter((s) => s.type === "image")
    .map((s) => {
      const name = tokenName(s.text);
      return cell[`${name}_url`] ?? cell[name];
    })
    .filter((url): url is string => typeof url === "string" && url.length > 0);
};
